import * as d3 from "d3";
import {
  Event,
  HistoricalGameOdds,
  Maybe,
  OddsSelection,
} from "@/graphql/gql/graphql";
import { LinePlotData } from "./LinePlot";

export const filterEmptyLineData = (line: LinePlotData): number[] => {
  return line.filter((dataPoint) => dataPoint != null);
};

export const makeAmericanOddsLinear = (line: number[]): number[] => {
  const formattedData = line.map((point) => {
    if (point > 0) {
      return point - 200;
    } else return point;
  });
  return formattedData;
};

export function formatAmericanOddsLabels(labelData: d3.NumberValue): string {
  const label = Number(labelData);
  if (label === -100) {
    return "EVEN";
  } else if (label > -100) {
    return `+${label + 200}`;
  } else {
    return label.toString();
  }
}

export function formatOddString(
  oddData: number | null | undefined | d3.NumberValue
): string {
  if (oddData == null) {
    return "-";
  }
  const odd = Number(oddData);
  if (odd > 0) {
    return `+${odd}`;
  }
  return odd.toString();
}

export interface FormattedOddsReturn {
  days: number[];
  homeMoneyLineOdds: number[];
  homeSpreadLine: number[];
  homeSpreadLineOdds: number[];
  totalLine: number[];
  totalOverOdds: number[];
  awayMoneyLineOdds: number[];
  awaySpreadLine: number[];
  awaySpreadLineOdds: number[];
  totalUnderOdds: number[];
}

export function deconstructHistoricalOdds(
  historicalOddsData: Event["historicalOdds"]
): FormattedOddsReturn {
  const toNumber = (
    dataPoint: Maybe<HistoricalGameOdds>,
    key: keyof HistoricalGameOdds
  ) => {
    if (dataPoint?.[key] != null) {
      return Number(dataPoint[key]);
    }
    return undefined;
  };

  const historicalOdds = historicalOddsData?.filter((dataPoint) => {
    return !dataPoint?.isLive;
  });
  const days = historicalOdds?.map((dataPoint) =>
    toNumber(dataPoint, "timestamp")
  );
  const homeMoneyLineOdds = historicalOdds?.map((dataPoint) =>
    toNumber(dataPoint, "homeMoneylineOdds")
  );
  const homeSpreadLine = historicalOdds?.map((dataPoint) =>
    toNumber(dataPoint, "homeSpread")
  );
  const homeSpreadLineOdds = historicalOdds?.map((dataPoint) =>
    toNumber(dataPoint, "homeSpreadOdds")
  );
  const totalLine = historicalOdds?.map((dataPoint) =>
    toNumber(dataPoint, "ouTotalPoints")
  );
  const totalOverOdds = historicalOdds?.map((dataPoint) =>
    toNumber(dataPoint, "overTotalPointsOdds")
  );
  const awayMoneyLineOdds = historicalOdds?.map((dataPoint) =>
    toNumber(dataPoint, "awayMoneylineOdds")
  );
  const awaySpreadLine = historicalOdds?.map((dataPoint) =>
    toNumber(dataPoint, "awaySpread")
  );
  const awaySpreadLineOdds = historicalOdds?.map((dataPoint) =>
    toNumber(dataPoint, "awaySpreadOdds")
  );
  const totalUnderOdds = historicalOdds?.map((dataPoint) =>
    toNumber(dataPoint, "underTotalPointsOdds")
  );

  return {
    days: filterEmptyLineData(days ?? []),
    homeMoneyLineOdds: filterEmptyLineData(homeMoneyLineOdds ?? []),
    homeSpreadLine: filterEmptyLineData(homeSpreadLine ?? []),
    homeSpreadLineOdds: filterEmptyLineData(homeSpreadLineOdds ?? []),
    totalLine: filterEmptyLineData(totalLine ?? []),
    totalOverOdds: filterEmptyLineData(totalOverOdds ?? []),
    awayMoneyLineOdds: filterEmptyLineData(awayMoneyLineOdds ?? []),
    awaySpreadLine: filterEmptyLineData(awaySpreadLine ?? []),
    awaySpreadLineOdds: filterEmptyLineData(awaySpreadLineOdds ?? []),
    totalUnderOdds: filterEmptyLineData(totalUnderOdds ?? []),
  };
}

export const sortOdds = (
  odds: Maybe<Array<Maybe<OddsSelection>>>,
  homeNfId: Maybe<string> | undefined,
  awayNfId: Maybe<string> | undefined
) => {
  const home = odds?.find((selection) => {
    return selection?.teamNumberFireId === Number(homeNfId);
  });
  const away = odds?.find((selection) => {
    return selection?.teamNumberFireId === Number(awayNfId);
  });
  return { home, away };
};
